import React, { type PropsWithChildren, type MouseEvent, createContext, useRef, useState, useContext, useMemo, useCallback, useEffect } from "react";
import { twMerge } from "tailwind-merge";

import { type TagProps, SupTag as Tag } from "./Tag";
import { SupButton } from "../Button";
import { SupText } from "../Text";

/** Types */
type Name = string | number;

type TagCommonProps = Pick<TagProps, "border" | "small" | "className">;

export type TagSelectProps = TagCommonProps & {
  className?: string;
  optionClassName?: string;
  onChange?: (values: Name[]) => void;
  selectAll?: boolean;
  selectAllClassName?: string;
};

export type TagOptionProps = TagProps & {
  name: string;
  defaultChecked?: boolean;
};

interface TagSelectContext {
  props?: TagCommonProps;
  registerTag: (name: Name, checked?: boolean) => void;
  unregisterTag: (name: Name) => void;
  updateTagState: (name: Name, checked: boolean) => void;
  getTagState: (name: Name) => boolean;
}

const Context = createContext<TagSelectContext>({
  registerTag: () => null,
  unregisterTag: () => null,
  updateTagState: () => null,
  getTagState: () => false,
});

export function useSupTagSelectContext() {
  return useContext(Context);
}

/** Option */
export function SupTagOption({ name, defaultChecked, ...tagProps }: TagOptionProps) {
  const { current: tag } = useRef({ name, defaultChecked });
  const { registerTag, unregisterTag, updateTagState, getTagState, props: commonProps } = useSupTagSelectContext();

  useEffect(() => {
    registerTag(tag.name, tag.defaultChecked);

    return () => {
      unregisterTag(tag.name);
    };
  }, [registerTag, unregisterTag]);

  const active = useMemo(() => getTagState(tag.name), [getTagState]);

  const handleClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      updateTagState(tag.name, !active);
      tagProps.onClick?.(e);
    },
    [updateTagState, active]
  );

  return <Tag {...tagProps} {...commonProps} active={active} inactive={!active} onClick={handleClick} />;
}

/** Select */
export function SupTagSelect({ children, border, small, className, optionClassName, onChange, selectAll, selectAllClassName }: PropsWithChildren<TagSelectProps>) {
  const [state, setState] = useState<Record<Name, boolean>>({});

  useEffect(() => {
    onChange?.(Object.keys(state).filter(key => state[key]));
  }, [state]);

  const registerTag = useCallback((name: Name, defaultValue = false) => {
    setState(state => ({ ...state, [name]: defaultValue }));
  }, []);

  const unregisterTag = useCallback((name: Name) => {
    setState(state => (Reflect.deleteProperty(state, name) ? { ...state } : state));
  }, []);

  const updateTagState = useCallback((name: Name, value: boolean) => {
    setState(state => ({ ...state, [name]: value }));
  }, []);

  const getTagState = useCallback((name: Name) => state[name], [state]);

  const commonProps = useMemo(() => ({ border, small, className: optionClassName }), [border, small, optionClassName]);

  const contextValue = useMemo<TagSelectContext>(
    () => ({
      props: commonProps,
      registerTag,
      unregisterTag,
      updateTagState,
      getTagState,
    }),
    [commonProps, getTagState, updateTagState, unregisterTag, registerTag]
  );

  const isAllSelected = useMemo(() => Object.values(state).every(Boolean), [state]);

  const onSelectAll = useCallback(() => {
    setState(currentState => {
      return Object.keys(currentState).reduce<Record<Name, boolean>>((acc, key) => {
        acc[key] = true;
        return acc;
      }, {});
    });
  }, []);

  return (
    <Context.Provider value={contextValue}>
      {selectAll && (
        <SupButton
          disabled={isAllSelected}
          variant="text"
          className={twMerge("underline-offset-2 hover:no-underline h-fit px-0 mb-3", isAllSelected ? "cursor-not-allowed" : "underline", selectAllClassName)}
          onClick={() => {
            onSelectAll();
          }}
        >
          <SupText variant="caption">Select All</SupText>
        </SupButton>
      )}
      <div className={twMerge("flex flex-wrap gap-x-2 gap-y-3", className)}>{children}</div>
    </Context.Provider>
  );
}
